import { knowYourCustomerApi } from "@/api";
import {
  type AuthenticationTypeSlug,
  type IAuthenticationDataRequest,
  type IAuthenticationDataResponse,
  type IAuthenticationTypeRules,
  type IAuthenticationTypes,
  type IContactInformation,
  type IEarningExpense,
  type IIncomeInformation,
  type IKnowYourCustomerCurrentForm,
  type IKnowYourCustomerOverview,
  type IKycFormStepResponse,
  type IPersonalInformation,
  type IPoliticalEffected,
  type IPoliticalEffectedPeople,
  type KycFormStep,
  type TKnowYourCustomerStepAction,
} from "./types";

export const getKycOverview = () =>
  knowYourCustomerApi
    .get<IKnowYourCustomerOverview>("/know-your-customers/overview")
    .then((r) => r.data);

export const getKycCurrentForm = () =>
  knowYourCustomerApi
    .get<IKnowYourCustomerCurrentForm>("/know-your-customer-forms/current")
    .then((r) => r.data);

export const getPersonalInformation = () =>
  knowYourCustomerApi
    .get<IPersonalInformation>("/know-your-customer-forms/personal-information")
    .then((r) => r.data);

export const submitPersonalInformation = (data: IPersonalInformation) =>
  knowYourCustomerApi
    .post<{
      currentForm: KycFormStep;
    }>("/know-your-customer-forms/personal-information", data)
    .then((r) => r.data?.currentForm);

export const getContactInformation = () =>
  knowYourCustomerApi
    .get<IContactInformation>("/know-your-customer-forms/contact-information")
    .then((r) => r.data);

export const submitContactInformation = (data: IContactInformation) =>
  knowYourCustomerApi
    .post<IKycFormStepResponse>(
      "/know-your-customer-forms/contact-information",
      data,
    )
    .then((r) => r.data);

export const getIncomeInformation = () =>
  knowYourCustomerApi
    .get<IIncomeInformation>("/know-your-customer-forms/income-information")
    .then((r) => r.data);

export const submitIncomeInformation = (data: IIncomeInformation) =>
  knowYourCustomerApi
    .post<IKycFormStepResponse>(
      "/know-your-customer-forms/income-information",
      data,
    )
    .then((r) => r.data);

export const getEarningExpense = () =>
  knowYourCustomerApi
    .get<IEarningExpense>("/know-your-customer-forms/earning-expense")
    .then((r) => r.data);

export const submitEarningExpense = (data: IEarningExpense) =>
  knowYourCustomerApi
    .post<IKycFormStepResponse>(
      "/know-your-customer-forms/earning-expense",
      data,
    )
    .then((r) => r.data);

export const getPoliticalEffected = () =>
  knowYourCustomerApi
    .get<IPoliticalEffected>("/know-your-customer-forms/political-effected")
    .then((r) => r.data);

export const submitPoliticalEffected = (data: IPoliticalEffected) =>
  knowYourCustomerApi
    .post<IKycFormStepResponse>(
      "/know-your-customer-forms/political-effected",
      data,
    )
    .then((r) => r.data);

export const getPoliticalEffectedPeople = () =>
  knowYourCustomerApi
    .get<IPoliticalEffectedPeople>(
      "/know-your-customer-forms/political-effected-people",
    )
    .then((r) => r.data);

export const submitPoliticalEffectedPeople = (data: IPoliticalEffectedPeople) =>
  knowYourCustomerApi
    .post<IKycFormStepResponse>(
      "/know-your-customer-forms/political-effected-people",
      data,
    )
    .then((r) => r.data);

export const confirmKnowYourCustomerForm = (
  step: TKnowYourCustomerStepAction,
) =>
  knowYourCustomerApi
    .post<IKnowYourCustomerCurrentForm>("/know-your-customer-forms/confirm", {
      step,
    })
    .then((r) => r.data);

export const getAuthenticationTypes = (authenticationPurposeType: string) =>
  knowYourCustomerApi
    .get<IAuthenticationTypes>(
      `/know-your-customer-forms/${authenticationPurposeType}`,
    )
    .then((r) => r.data);

export const getAuthenticationTypeRules = (
  authenticationPurpose: string,
  authenticationType: string,
) =>
  knowYourCustomerApi
    .get<IAuthenticationTypeRules>(
      `/know-your-customer-forms/${authenticationPurpose}/${authenticationType}`,
    )
    .then((r) => r.data);

export const submitAuthenticationData = (
  authenticationPurpose: string,
  authenticationTypeSlug: AuthenticationTypeSlug,
  data?: IAuthenticationDataRequest,
) =>
  knowYourCustomerApi
    .post<IAuthenticationDataResponse>(
      `/${authenticationPurpose}/${authenticationTypeSlug}`,
      data,
      { timeout: 0 },
    )
    .then((r) => r.data);

export const getAuthenticationStatus = (
  authenticationPurpose: string,
  authenticationTypeSlug: AuthenticationTypeSlug,
  sessionId?: string,
) =>
  knowYourCustomerApi
    .get<{
      state: string;
    }>(
      `/${authenticationPurpose}/${authenticationTypeSlug}/${sessionId}/status`,
    )
    .then((r) => r.data.state);

export const getKnowYourCustomerSurveyFile = () =>
  knowYourCustomerApi
    .get<{
      content: string;
      fileName: string;
    }>("/know-your-customer-files/current")
    .then((r) => ({ data: r.data.content, fileName: r.data.fileName }));

export const submitPreviousFormData = () =>
  knowYourCustomerApi
    .post<{
      currentForm: KycFormStep;
    }>(`/know-your-customer-forms/previous-form`)
    .then((r) => r.data.currentForm);

export const confirmBiometricSigning = () =>
  knowYourCustomerApi
    .post<{
      currentForm: KycFormStep;
    }>(`/document-signings/biometric`)
    .then((r) => r.data);
